import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuthStore, useRenderStore } from "@/store";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSelector from "@/components/LanguageSelector";
import RenderThumbnail from "@/components/RenderThumbnail";
import {
  Download,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  User,
  LogOut,
  Settings,
  Crown,
  Layers,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { user, logout } = useAuthStore();
  const navigate = useLocalizedNavigation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <LocalizedLink to="/" className="flex items-center gap-2">
            <img 
              src="/assets/logo-creavideos.png" 
              alt="CreaVideos" 
              className="h-8 w-auto"
            />
          </LocalizedLink>

          <div className="flex items-center gap-4">
            <LanguageSelector />
            <LocalizedLink to="/templates">
              <Button variant="ghost" className="hidden md:flex">
                <Layers className="w-4 h-4 mr-2" />
                Templates
              </Button>
            </LocalizedLink>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-full hover:bg-slate-100">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-teal-600" />
                  </div>
                  <span className="hidden md:block font-medium text-sm">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <LocalizedLink to="/account" className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Account
                  </LocalizedLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <LocalizedLink to="/subscription" className="cursor-pointer">
                    <Crown className="w-4 h-4 mr-2" />
                    Subscription
                  </LocalizedLink>
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default function RendersListPage() {
  const { user } = useAuthStore();
  const { renders, fetchRenders, deleteRender, isLoading } = useRenderStore();
  const navigate = useLocalizedNavigation();
  const [filter, setFilter] = useState("all"); // all, completed, processing, failed

  useEffect(() => {
    fetchRenders();
  }, [fetchRenders]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "processing":
      case "queued":
        return <Loader2 className="w-5 h-5 text-violet-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const handleDeleteRender = async (event, renderId) => {
    event.stopPropagation();

    const confirmed = window.confirm("Delete this render? This action cannot be undone.");
    if (!confirmed) return;

    await deleteRender(renderId);
  };

  const filteredRenders = renders
    .filter(render => {
      if (filter === "all") return true;
      return render.status === filter;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">All Renders</h1>
              <p className="text-slate-600">View and manage all your video renders <span className="text-xs text-slate-500">(Renders are automatically deleted after 2 months)</span></p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              onClick={() => setFilter("all")}
              size="sm"
            >
              All ({renders.length})
            </Button>
            <Button
              variant={filter === "completed" ? "default" : "outline"}
              onClick={() => setFilter("completed")}
              size="sm"
            >
              Completed ({renders.filter(r => r.status === "completed").length})
            </Button>
            <Button
              variant={filter === "processing" ? "default" : "outline"}
              onClick={() => setFilter("processing")}
              size="sm"
            >
              Processing ({renders.filter(r => r.status === "processing" || r.status === "queued").length})
            </Button>
            <Button
              variant={filter === "failed" ? "default" : "outline"}
              onClick={() => setFilter("failed")}
              size="sm"
            >
              Failed ({renders.filter(r => r.status === "failed").length})
            </Button>
          </div>

          {/* Renders Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : filteredRenders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {filter === "all" ? "No renders yet" : `No ${filter} renders`}
              </h3>
              <p className="text-slate-600 mb-4">
                {filter === "all" 
                  ? "Start by selecting a template and creating your first video"
                  : `You don't have any ${filter} renders at the moment`
                }
              </p>
              {filter === "all" && (
                <LocalizedLink to="/templates">
                  <Button className="btn-primary rounded-full">
                    Browse Templates
                  </Button>
                </LocalizedLink>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRenders.map((render) => (
                <div
                  key={render.id}
                  className="flex gap-3 bg-white rounded-xl border border-slate-200 p-1.5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/renders/${render.id}`)}
                >
                  <RenderThumbnail render={render} />
                  <div className="min-w-0 flex-1 py-2 pr-2">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(render.status)}
                        <span className="font-medium capitalize">{render.status}</span>
                      </div>
                      <span className="text-sm text-slate-500">
                        {new Date(render.created_at).toLocaleDateString()} {new Date(render.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                  </div>
                  
                  {render.status === "processing" && (
                    <Progress value={render.progress || 0} className="h-1.5 mb-3" />
                  )}
                  
                  {render.status === "failed" && render.error && (
                    <p className="text-xs text-red-600 mb-2">{render.error}</p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      ID: {render.id.slice(0, 8)}...
                    </span>
                    {render.status === "completed" && render.output_url && (
                      <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          asChild
                        >
                          <a href={render.output_url} download>
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </a>
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={(event) => handleDeleteRender(event, render.id)}
                          aria-label="Delete render"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
